import { useEffect, useMemo, useRef } from 'react';
import { FileText, RefreshCw, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

import { validatePdf } from '../lib/invoice-pdf-upload';
import { OpenPdfInNewWindow } from './open-pdf-new-window';

function CreatePreview({ url, name }: Readonly<{ url: string | null; name: string | null }>) {
  if (url === null) {
    return (
      <div
        className="bg-muted/30 text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center text-sm"
        data-testid="invoice-create-pdf-empty"
      >
        <FileText className="size-8" aria-hidden />
        <p>Attach a supplier PDF — it&apos;s linked to the invoice when you save.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col gap-2">
      <iframe
        src={url}
        title="Invoice PDF preview"
        className="min-h-[18rem] flex-1 rounded-md border"
        data-testid="invoice-create-pdf-preview"
      />
      <div className="flex flex-wrap items-center gap-2">
        <OpenPdfInNewWindow url={url} />
        {name === null ? null : <span className="text-muted-foreground text-xs">{name}</span>}
      </div>
    </div>
  );
}

function CreateActions({
  hasFile,
  onUpload,
  onRemove,
}: Readonly<{ hasFile: boolean; onUpload: () => void; onRemove: () => void }>) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="invoice-create-pdf-upload"
        onClick={onUpload}
      >
        {hasFile ? (
          <RefreshCw className="size-4" aria-hidden />
        ) : (
          <Upload className="size-4" aria-hidden />
        )}
        {hasFile ? 'Replace' : 'Upload PDF'}
      </Button>
      {hasFile ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          data-testid="invoice-create-pdf-remove"
          onClick={onRemove}
        >
          <Trash2 className="size-4" aria-hidden /> Remove
        </Button>
      ) : null}
    </div>
  );
}

export interface InvoiceCreatePdfPickerProps {
  readonly file: File | null;
  readonly onFileChange: (file: File | null) => void;
}

// Create-mode counterpart of InvoicePdfPanel: holds the chosen PDF locally
// (previewed via an object URL) and hands it to the parent. The actual upload
// happens after the invoice is created, against the new invoice id.
export function InvoiceCreatePdfPicker({ file, onFileChange }: InvoiceCreatePdfPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    },
    [objectUrl],
  );

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    const err = validatePdf(picked);
    if (err !== null) {
      toast.error(err);
      return;
    }
    onFileChange(picked);
  };

  return (
    <section
      aria-labelledby="invoice-create-pdf-heading"
      className="flex min-h-0 flex-1 flex-col gap-3"
    >
      <h3 id="invoice-create-pdf-heading" className="text-sm font-semibold">
        Invoice PDF
      </h3>
      <CreatePreview url={objectUrl} name={file?.name ?? null} />
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        data-testid="invoice-create-pdf-file-input"
        onChange={onPick}
      />
      <CreateActions
        hasFile={file !== null}
        onUpload={() => {
          fileInputRef.current?.click();
        }}
        onRemove={() => {
          onFileChange(null);
        }}
      />
    </section>
  );
}
